import React from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, Dimensions, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width } = Dimensions.get('window');

const ReportIssueScreen = () => {
    const { colors } = useTheme();
    const isMobile = width < 768;

    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        issueType: '',
        page: '',
        description: '',
        steps: '',
    });

    const issueTypes = [
        { id: 'bug', label: 'Bug / Error', icon: 'bug' },
        { id: 'calculation', label: 'Wrong Calculation', icon: 'calculator' },
        { id: 'ui', label: 'UI Issue', icon: 'phone-portrait' },
        { id: 'performance', label: 'Slow Performance', icon: 'speedometer' },
        { id: 'other', label: 'Other', icon: 'help-circle' },
    ];

    const handleSubmit = () => {
        const mailtoUrl = `mailto:shubham@modernmunshiji.com?subject=Issue Report: ${formData.issueType}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nIssue Type: ${formData.issueType}\nAffected Page: ${formData.page}\n\nDescription:\n${formData.description}\n\nSteps to Reproduce:\n${formData.steps}`)}`;
        Linking.openURL(mailtoUrl);
    };

    const styles = StyleSheet.create({
        container: { flex: 1, backgroundColor: colors.background },
        scrollView: { flex: 1 },
        hero: {
            backgroundColor: '#1a1a4e',
            paddingVertical: 60,
            paddingHorizontal: 20,
            alignItems: 'center',
        },
        heroTitle: {
            fontSize: isMobile ? 28 : 40,
            fontWeight: '800',
            color: '#c9a44e',
            textAlign: 'center',
            marginBottom: 12,
        },
        heroSubtitle: {
            fontSize: 16,
            color: 'rgba(255,255,255,0.8)',
            textAlign: 'center',
            maxWidth: 600,
        },
        content: {
            padding: 20,
            maxWidth: 700,
            alignSelf: 'center',
            width: '100%',
        },
        infoCard: {
            backgroundColor: '#fef3c7',
            borderRadius: 10,
            padding: 16,
            marginBottom: 24,
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 12,
        },
        infoText: {
            flex: 1,
            fontSize: 14,
            color: '#92400e',
            lineHeight: 20,
        },
        formCard: {
            backgroundColor: colors.surface,
            borderRadius: 16,
            padding: 30,
            borderWidth: 1,
            borderColor: colors.border,
        },
        inputGroup: {
            marginBottom: 20,
        },
        label: {
            fontSize: 14,
            fontWeight: '600',
            color: colors.text,
            marginBottom: 10,
        },
        input: {
            backgroundColor: colors.background,
            borderWidth: 1,
            borderColor: colors.border,
            borderRadius: 8,
            paddingHorizontal: 16,
            paddingVertical: 14,
            fontSize: 15,
            color: colors.text,
        },
        typeRow: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 10,
        },
        typeButton: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 10,
            paddingHorizontal: 14,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.background,
            gap: 6,
        },
        typeButtonSelected: {
            backgroundColor: '#dc2626',
            borderColor: '#dc2626',
        },
        typeButtonText: {
            fontSize: 13,
            color: colors.text,
        },
        typeButtonTextSelected: {
            color: '#fff',
        },
        textArea: {
            height: 100,
            textAlignVertical: 'top',
        },
        submitButton: {
            backgroundColor: '#dc2626',
            paddingVertical: 16,
            borderRadius: 8,
            alignItems: 'center',
            marginTop: 10,
        },
        submitButtonText: {
            color: '#fff',
            fontWeight: '700',
            fontSize: 16,
        },
    });

    return (
        <View style={styles.container}>
            <Header />
            <ScrollView style={styles.scrollView}>
                <View style={styles.hero}>
                    <Text style={styles.heroTitle}>Report an Issue</Text>
                    <Text style={styles.heroSubtitle}>
                        Found something that doesn't work? Let us know so we can fix it quickly.
                    </Text>
                </View>

                <View style={styles.content}>
                    <View style={styles.infoCard}>
                        <Ionicons name="information-circle" size={22} color="#92400e" />
                        <Text style={styles.infoText}>
                            Please provide as much detail as possible to help us identify and fix the issue. Include steps to reproduce if possible.
                        </Text>
                    </View>

                    <View style={styles.formCard}>
                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Your Name</Text>
                            <TextInput
                                style={styles.input}
                                placeholder="Enter your name"
                                placeholderTextColor={colors.textSecondary}
                                value={formData.name}
                                onChangeText={(text) => setFormData({ ...formData, name: text })}
                            />
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Email Address</Text>
                            <TextInput
                                style={styles.input}
                                placeholder="Enter your email"
                                placeholderTextColor={colors.textSecondary}
                                keyboardType="email-address"
                                value={formData.email}
                                onChangeText={(text) => setFormData({ ...formData, email: text })}
                            />
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Type of Issue</Text>
                            <View style={styles.typeRow}>
                                {issueTypes.map((type) => (
                                    <TouchableOpacity
                                        key={type.id}
                                        style={[
                                            styles.typeButton,
                                            formData.issueType === type.id && styles.typeButtonSelected,
                                        ]}
                                        onPress={() => setFormData({ ...formData, issueType: type.id })}
                                    >
                                        <Ionicons
                                            name={type.icon}
                                            size={16}
                                            color={formData.issueType === type.id ? '#fff' : colors.textSecondary}
                                        />
                                        <Text
                                            style={[
                                                styles.typeButtonText,
                                                formData.issueType === type.id && styles.typeButtonTextSelected,
                                            ]}
                                        >
                                            {type.label}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Which page has the issue?</Text>
                            <TextInput
                                style={styles.input}
                                placeholder="e.g., EMI Calculator, Invoice Generator"
                                placeholderTextColor={colors.textSecondary}
                                value={formData.page}
                                onChangeText={(text) => setFormData({ ...formData, page: text })}
                            />
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Describe the Issue</Text>
                            <TextInput
                                style={[styles.input, styles.textArea]}
                                placeholder="What happened? What did you expect?"
                                placeholderTextColor={colors.textSecondary}
                                multiline
                                value={formData.description}
                                onChangeText={(text) => setFormData({ ...formData, description: text })}
                            />
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Steps to Reproduce (optional)</Text>
                            <TextInput
                                style={[styles.input, styles.textArea]}
                                placeholder="1. Go to...\n2. Click on...\n3. See error"
                                placeholderTextColor={colors.textSecondary}
                                multiline
                                value={formData.steps}
                                onChangeText={(text) => setFormData({ ...formData, steps: text })}
                            />
                        </View>

                        <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
                            <Text style={styles.submitButtonText}>Submit Issue Report</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                <Footer />
            </ScrollView>
        </View>
    );
};

export default ReportIssueScreen;
